import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE,
  LINKEDIN_PERSONAL_ACCOUNT,
  TIKTOK_BUSINESS_PAGE,
  TIKTOK_PERSONAL_ACCOUNT,
  TWITTER,
  YOUTUBE
} from '@/config/constants';
import { getDateTimeFormated, getTimeFormated } from '../utils/dates/utils';

interface Post {
  id: string;
  type: string;
  media: { url: string; file_type: string }[];
  caption: string;
  analytics: {
    comments: number;
    reactions: number;
    impressions: number;
    interactions: number;
    engagement_rate: number;
  };

  permalink: string;
  published: string;
  image_preview_url: string | null;
}

export function engagementRateByFollower(
  posts: Post[],
  followerCount: number,
  postCount: number
): string | number {
  if (!posts.length || followerCount <= 0) return 0;

  const totalInteractions = posts.reduce((sum, post) => {
    return sum + (post.analytics?.interactions || 0);
  }, 0);

  const engagementRate =
    (totalInteractions / (followerCount * postCount)) * 100;

  // Round to 2 decimal places
  return `${parseFloat(engagementRate.toFixed(2))}%`;
}

export function engagementRatePerPost(
  posts: Post[],
  postCount: number
): number {
  if (!posts.length || postCount <= 0) return 0;

  const totalInteractions = posts.reduce((sum, post) => {
    return sum + (post.analytics?.interactions || 0);
  }, 0);

  const engagementRate = totalInteractions / postCount;

  // Round to 2 decimal places
  return parseFloat(engagementRate.toFixed(2));
}

// Type definitions
interface Media {
  url: string;
  file_type: 'image' | 'video';
}

interface Analytics {
  comments?: number;
  reactions?: number;
  impressions?: number | null;
  interactions?: number;
  engagement_rate?: number;
  comment_count?: number;
  hashtags?: number;
}

interface PostData {
  id: string;
  type: 'post' | 'reel' | 'video' | 'multiimage' | 'story';
  media?: Media[];
  message: string;
  caption: string;
  analytics?: Analytics;
  permalink: string;
  published: string;
  image_preview_url?: string;
}

export interface CompetitorData {
  uuid: string;
  platform: string;
  platform_id: string;
  username: string;
  name: string;
  profile_picture: string;
  followers_count: number;
  follows_count: number;
  media_count: number;
  posts_data: PostData[];
  followers_development: {
    date: string;
    value: number;
  }[];
}

interface Column {
  title: string;
  image: string;
}

interface Row {
  title: string;
  moreInfo?: string;
  values: (string | number)[];
}

interface Section {
  title: string;
  rows: Row[];
}

export interface TransformedData {
  title: string;
  columns: Column[];
  sections: Section[];
}

interface PostTypeCounts {
  reel: number;
  video: number;
  multiimage: number;
  singleimage: number;
  post: number;
}

export function transformCompetitorData(
  results: CompetitorData[],
  t: any
): TransformedData {
  // Helper function to calculate post type counts
  function getPostTypeCounts(posts: PostData[]): PostTypeCounts {
    const counts: PostTypeCounts = {
      reel: 0,
      video: 0,
      multiimage: 0,
      singleimage: 0,
      post: 0
    };

    posts.forEach((post) => {
      // Count by post type
      if (post.type === 'reel') {
        counts.reel++;
      } else if (post.type === 'post') {
        counts.post++;
      }

      // Count by media type
      if (post.media && post.media.length > 0) {
        const hasVideo = post.media.some((m) => m.file_type === 'video');
        const imageCount = post.media.filter(
          (m) => m.file_type === 'image'
        ).length;

        if (hasVideo) {
          counts.video++;
        } else if (imageCount > 1) {
          counts.multiimage++;
        } else if (imageCount === 1) {
          counts.singleimage++;
        }
      }
    });

    return counts;
  }

  // Helper function to find most common posting day/time
  function getMostCommonPostTime(posts: PostData[]): string {
    const dayTimeMap: Record<string, number> = {};

    posts.forEach((post) => {
      if (!post.published) return;

      const date = new Date(post.published);

      const dayNames = [
        t('monday'),
        t('tuesday'),
        t('wednesday'),
        t('thursday'),
        t('friday'),
        t('saturday'),
        t('sunday')
      ];

      const day = dayNames[(date.getDay() + 6) % 7];

      // Round to the HOUR
      const hour = date.getHours(); // 0–23
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12; // 1–12

      // Key groups everything posted in the same hour
      const timeStr = `${day} ${t(
        'competitorAnalytics.at'
      )} ${displayHour}:00 ${ampm}`;

      dayTimeMap[timeStr] = (dayTimeMap[timeStr] || 0) + 1;
    });

    let mostCommon = 'N/A';
    let maxCount = 0;
    console.log('dayTimeMap', dayTimeMap, posts);
    for (const [timeStr, count] of Object.entries(dayTimeMap)) {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = timeStr;
      }
    }

    return mostCommon;
  }

  // Helper function to get latest post date
  function getLatestPostDate(posts: PostData[]): string {
    if (posts.length === 0) return 'N/A';

    const latestPost = posts.reduce((latest, post) => {
      const postDate = new Date(post.published);
      const latestDate = new Date(latest.published);
      return postDate > latestDate ? post : latest;
    });

    const date = new Date(latestPost.published);
    return `${getDateTimeFormated(date, true)}`;
  }

  // Helper function to get most used post type
  function getMostUsedPostType(posts: PostData[], t: any): string {
    const typeCounts: Record<string, number> = {};
    posts.forEach((post) => {
      typeCounts[post.type] = (typeCounts[post.type] || 0) + 1;
    });

    let mostUsed = 'post';
    let maxCount = 0;

    for (const [type, count] of Object.entries(typeCounts)) {
      if (count > maxCount) {
        maxCount = count;
        mostUsed = type;
      }
    }

    return `${t(mostUsed)}`;
  }

  // Process each competitor
  const columns: Column[] = results.map((competitor) => ({
    title: competitor.name || competitor.username || 'Unnamed',
    image: competitor.profile_picture || ''
  }));

  // Calculate metrics for each competitor
  const competitorMetrics = results.map((competitor) => {
    const posts = competitor.posts_data || [];
    const postCounts = getPostTypeCounts(posts);

    // Community metrics
    const totalFollowers = competitor.followers_count || 0;
    // --- Followers Development ---
    let followerDevelopment = 0;
    let followerDevelopmentPercentage = '-';
    if (
      competitor.followers_development &&
      competitor.followers_development.length > 1
    ) {
      const sortedFollowers = [...competitor.followers_development].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      const earliest = sortedFollowers[0].value;
      const latest = sortedFollowers[sortedFollowers.length - 1].value;

      const development = latest - earliest;
      followerDevelopment = development;

      if (earliest > 0) {
        const percentage = ((development / earliest) * 100).toFixed(2) + '%';
        followerDevelopmentPercentage = percentage;
      } else {
        followerDevelopmentPercentage = 'N/A'; // avoid division by zero
      }
    }

    // Content metrics
    const totalPosts = posts.length;
    console.log('Competitor', competitor.name);
    const mostCommonTime = getMostCommonPostTime(posts);
    const latestPost = getLatestPostDate(posts);
    const mostUsedType = getMostUsedPostType(posts, t);

    // Engagement metrics
    let totalInteractions = 0;
    let totalComments = 0;
    let totalReactions = 0;
    let totalEngagementRate = 0;
    let totalHashtags = 0;

    posts.forEach((post) => {
      const analytics = post.analytics || {};
      const interactions = analytics.interactions || 0;
      // (analytics.reactions || 0) +
      // (analytics.comments || 0) +
      // (analytics.comment_count || 0);
      const comments = analytics.comments || analytics.comment_count || 0;
      const reactions = analytics.reactions || 0;
      const engagementRate = analytics.engagement_rate || 0;
      const hashtags = analytics.hashtags || 0;

      totalInteractions += interactions;
      totalComments += comments;
      totalReactions += reactions;
      totalEngagementRate += engagementRate;
      totalHashtags += hashtags;
    });

    const avgEngagementRate =
      totalPosts > 0
        ? (totalEngagementRate / totalPosts).toFixed(2) + '%'
        : '0%';
    const avgInteractions =
      totalPosts > 0 ? (totalInteractions / totalPosts).toFixed(1) : '0';
    const avgReactions =
      totalPosts > 0 ? (totalReactions / totalPosts).toFixed(1) : '0';
    const avgComments =
      totalPosts > 0 ? (totalComments / totalPosts).toFixed(1) : '0';

    const avgHashtags =
      totalHashtags > 0 ? (totalHashtags / totalPosts).toFixed(1) : '0';
    const overallEngagementRate =
      totalFollowers > 0
        ? ((totalInteractions / totalFollowers) * 100).toFixed(2) + '%'
        : '0%';

    return {
      // Community
      totalFollowers: totalFollowers,
      followerDevelopment,
      followerDevelopmentPercentage,
      // Content
      totalPosts: totalPosts,
      mostCommonTime,
      latestPost,
      mostUsedType,
      reels: postCounts.reel,
      videos: postCounts.video,
      multiimage: postCounts.multiimage,
      singleimage: postCounts.singleimage,

      // Engagement
      engagementRate: overallEngagementRate,
      interactions: totalInteractions,
      reaction: totalReactions,
      comments: totalComments,
      totalHashtags: totalHashtags,
      avgEngagementRate,
      avgInteractions: parseFloat(avgInteractions),
      avgReactions: parseFloat(avgReactions),
      avgComments: parseFloat(avgComments),
      avgHashtags: parseFloat(avgHashtags)
    };
  });

  // Build the final structure
  const transformedData: TransformedData = {
    title: t('lomavisCompetitorAnalysis.comparative_table'),

    columns,
    sections: [
      {
        title: t('lomavisCompetitorAnalysis.community'),
        rows: [
          {
            title: t('lomavisCompetitorAnalysis.total_number_of_followers'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.total_number_of_followers.moreInfo'
            ),
            values: competitorMetrics.map((m) => m.totalFollowers)
          },
          {
            title: t('lomavisCompetitorAnalysis.follower_evolution'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.follower_evolution.moreInfo'
            ),
            values: competitorMetrics.map((m) => m.followerDevelopment) // Placeholder
          },
          {
            title: t('lomavisCompetitorAnalysis.follower_evolution_percentage'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.follower_evolution_percentage.moreInfo'
            ),
            values: competitorMetrics.map(
              (m) => m.followerDevelopmentPercentage
            ) // Placeholder
          }
        ]
      },
      {
        title: t('lomavisCompetitorAnalysis.content'),

        rows: [
          {
            title: t('lomavisCompetitorAnalysis.total_posts'),
            moreInfo: t('lomavisCompetitorAnalysis.total_posts.moreInfo'),
            values: competitorMetrics.map((m) => m.totalPosts)
          },
          {
            title: t('lomavisCompetitorAnalysis.posts_the_most_on'),
            moreInfo: t('lomavisCompetitorAnalysis.posts_the_most_on.moreInfo'),
            values: competitorMetrics.map((m) => m.mostCommonTime)
          },
          {
            title: t('lomavisCompetitorAnalysis.latest_post_on'),
            moreInfo: t('lomavisCompetitorAnalysis.latest_post_on.moreInfo'),
            values: competitorMetrics.map((m) => m.latestPost)
          },
          {
            title: t('lomavisCompetitorAnalysis.most_used_post_type'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.most_used_post_type.moreInfo'
            ),
            values: competitorMetrics.map((m) => m.mostUsedType)
          },
          {
            title: t('lomavisCompetitorAnalysis.reels'),
            moreInfo: t('lomavisCompetitorAnalysis.reels.moreInfo'),
            values: competitorMetrics.map((m) => m.reels)
          },
          {
            title: t('lomavisCompetitorAnalysis.videos'),
            moreInfo: t('lomavisCompetitorAnalysis.videos.moreInfo'),
            values: competitorMetrics.map((m) => m.videos)
          },
          {
            title: t('lomavisCompetitorAnalysis.multiimage'),
            moreInfo: t('lomavisCompetitorAnalysis.multiimage.moreInfo'),
            values: competitorMetrics.map((m) => m.multiimage)
          },
          {
            title: t('lomavisCompetitorAnalysis.single_image'),
            moreInfo: t('lomavisCompetitorAnalysis.single_image.moreInfo'),
            values: competitorMetrics.map((m) => m.singleimage)
          }
        ]
      },
      {
        title: t('lomavisCompetitorAnalysis.engagement'),
        rows: [
          {
            title: t('lomavisCompetitorAnalysis.engagement_rate'),
            moreInfo: t('lomavisCompetitorAnalysis.engagement_rate.moreInfo'),
            values: competitorMetrics.map((m) => m.engagementRate)
          },
          {
            title: t('lomavisCompetitorAnalysis.interactions'),
            moreInfo: t('lomavisCompetitorAnalysis.interactions.moreInfo'),
            values: competitorMetrics.map((m) => m.interactions)
          },
          {
            title: t('lomavisCompetitorAnalysis.reactions'),
            moreInfo: t('lomavisCompetitorAnalysis.reactions.moreInfo'),
            values: competitorMetrics.map((m) => m.reaction)
          },
          {
            title: t('lomavisCompetitorAnalysis.comments'),
            moreInfo: t('lomavisCompetitorAnalysis.comments.moreInfo'),
            values: competitorMetrics.map((m) => m.comments)
          },
          {
            title: t('lomavisCompetitorAnalysis.total_number_of_hashtags'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.total_number_of_hashtags.moreInfo'
            ),
            values: competitorMetrics.map((m) => m.totalHashtags)
          },
          {
            title: t('lomavisCompetitorAnalysis.average_engagement_rate'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.average_engagement_rate.moreInfo'
            ),
            values: competitorMetrics.map((m) => m.avgEngagementRate)
          },
          {
            title: t('lomavisCompetitorAnalysis.avg_interactions'),
            moreInfo: t('lomavisCompetitorAnalysis.avg_interactions.moreInfo'),
            values: competitorMetrics.map((m) => m.avgInteractions)
          },
          {
            title: t('lomavisCompetitorAnalysis.avg_reactions'),
            moreInfo: t('lomavisCompetitorAnalysis.avg_reactions.moreInfo'),
            values: competitorMetrics.map((m) => m.avgReactions)
          },
          {
            title: t('lomavisCompetitorAnalysis.avg_comments'),
            moreInfo: t('lomavisCompetitorAnalysis.avg_commentd.moreInfo'),
            values: competitorMetrics.map((m) => m.avgComments)
          },
          {
            title: t('lomavisCompetitorAnalysis.average_number_of_hashtags'),
            moreInfo: t(
              'lomavisCompetitorAnalysis.average_number_of_hashtags.moreInfo'
            ),
            values: competitorMetrics.map((m) => m.avgHashtags)
          }
        ]
      }
    ]
  };
  return transformedData;
}

// Minimal type definitions - only keys used by the component
export interface MinimalAnalytics {
  sum_impressions: number | null;
  sum_reach: number | null;
  sum_reactions: number;

  sum_comments: number;
  sum_shares: number | null;
  sum_engagement: number;
  sum_interactions?: number | null;
}

export interface MinimalFormattedPostData {
  preview_image_url: string;
  text: string;
  publication_date: string;

  profile?: {
    name: string;
    imageUrl: string;
  };
  analytics: MinimalAnalytics;
  // Platform flags - only the ones that might be used
  facebook_post: boolean;
  instagram_post: boolean;
  twitter_post: boolean | null;
  linkedin_business_page_post: boolean | null;
  youtube_post: boolean | null;
  tiktok_business_page_post: boolean | null;
  linkedin_personal_account_post: boolean | null;
  tiktok_personal_account_post: boolean | null;
}

export interface SortOption {
  name: string;
  title: string;
}

export interface ComponentProps {
  title: string;
  data: MinimalFormattedPostData[];
  sortOptions: SortOption[];
}

// Input types (competitor data structure)
export interface CompetitorMedia {
  url: string;
  file_type: 'image' | 'video';
}

export interface CompetitorAnalytics {
  comments?: number;
  reactions?: number;
  impressions?: number | null;
  interactions?: number;
  engagement_rate?: number;
  comment_count?: number;
}

interface CompetitorPostData {
  id: string;
  type: 'post' | 'reel' | 'video';
  media?: CompetitorMedia[];
  message: string;
  analytics?: CompetitorAnalytics;
  permalink: string;
  published: string;
  image_preview_url?: string;
}

export function formatCompetitorDataForComponent(
  competitors: CompetitorData[],
  t: any
): ComponentProps {
  // Flatten all posts from all competitors
  const allPosts: MinimalFormattedPostData[] = [];

  competitors.forEach((competitor) => {
    competitor.posts_data.forEach((post) => {
      const analytics = post.analytics || {};

      // Calculate total engagement
      const totalInteractions = analytics.interactions || 0;
      const totalReactions = analytics.reactions || 0;
      const totalComments = analytics.comments || analytics.comment_count || 0;
      const totalEngagement =
        totalInteractions + totalReactions + totalComments;

      // Get preview image
      let previewImage = post.image_preview_url || '';
      if (!previewImage && post.media && post.media.length > 0) {
        previewImage = post.media[0].url;
      }

      // Set platform flags based on competitor platform
      const platform = competitor.platform.toLowerCase();

      const formattedPost: MinimalFormattedPostData = {
        preview_image_url: previewImage,
        text: post.caption || post.message || '',

        profile: {
          name: competitor?.name,
          imageUrl: competitor?.profile_picture
        },
        publication_date: post.published,
        analytics: {
          sum_impressions: analytics.impressions || null,
          sum_reach: null, // Not available in competitor data
          sum_reactions: analytics.reactions || 0,
          sum_comments: analytics.comments || analytics.comment_count || 0,
          sum_shares: null, // Not available in competitor data
          sum_engagement: analytics.engagement_rate,
          sum_interactions: analytics.interactions || 0
        },
        facebook_post: platform === FACEBOOK,
        instagram_post: platform === INSTAGRAM,
        twitter_post: platform === TWITTER ? true : null,
        linkedin_personal_account_post:
          platform === LINKEDIN_PERSONAL_ACCOUNT ? true : null,
        linkedin_business_page_post:
          platform === LINKEDIN_BUSINESS_PAGE ? true : null,
        youtube_post: platform === YOUTUBE ? true : null,
        tiktok_business_page_post:
          platform === TIKTOK_BUSINESS_PAGE ? true : null,
        tiktok_personal_account_post:
          platform === TIKTOK_PERSONAL_ACCOUNT ? true : null
      };

      allPosts.push(formattedPost);
    });
  });

  // Sort options that work with the component's sorting logic
  const sortOptions: SortOption[] = [
    {
      name: 'engagement',
      title: t('lomavisCompetitorAnalysis.engagement')
    },
    { name: 'impressions', title: t('lomavisCompetitorAnalysis.impressions') },
    { name: 'reactions', title: t('lomavisCompetitorAnalysis.reactions') },
    { name: 'comments', title: t('lomavisCompetitorAnalysis.comments') }
    // { name: 'reach', title: 'Reach' },
    // { name: 'shares', title: 'Shares' }
  ];

  return {
    title: 'Best Performing Competitor Posts',
    data: allPosts,
    sortOptions
  };
}

interface ChartDataPoint {
  x: string; // Date in YYYY-MM-DD format
  y: number; // Post count for that date
}

interface ChartSeries {
  name: string; // competitor name
  data: ChartDataPoint[];
}

export function collatePostsByDate(
  competitors: CompetitorData[]
): ChartSeries[] {
  return competitors.map((competitor) => {
    const datePostCounts: Record<string, number> = {};

    competitor.posts_data.forEach((post) => {
      // Extract date from published timestamp (YYYY-MM-DD format)
      const publishedDate = getDateTimeFormated(post.published, true);

      // Increment post count for this date
      datePostCounts[publishedDate] = (datePostCounts[publishedDate] || 0) + 1;
    });

    // Convert to array format and sort by date
    const data: ChartDataPoint[] = Object.entries(datePostCounts)
      .map(([date, count]) => ({
        x: date,
        y: count
      }))
      .sort((a, b) => a.x.localeCompare(b.x));

    return {
      name: competitor.name, // use competitor name for the legend/line label
      data
    };
  });
}

export function collateFollowersDevelopmentByDate(
  competitors: CompetitorData[]
): { name: string; data: ChartDataPoint[] }[] {
  return competitors.map((competitor) => {
    const dateFollowerCounts: Record<string, number> = {};

    competitor.followers_development?.forEach((entry) => {
      const formattedDate = getDateTimeFormated(entry.date, true);

      // If multiple entries exist for the same date, sum them (or replace — adjust logic as needed)
      dateFollowerCounts[formattedDate] =
        (dateFollowerCounts[formattedDate] || 0) + entry.value;
    });

    const data: ChartDataPoint[] = Object.entries(dateFollowerCounts)
      .map(([date, value]) => ({
        x: date,
        y: value
      }))
      .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

    return {
      name: competitor.name,
      data
    };
  });
}

export function collateInteractionsByDate(
  competitors: CompetitorData[]
): { name: string; data: ChartDataPoint[] }[] {
  return competitors.map((competitor) => {
    const dateInteractionCounts: Record<string, number> = {};

    competitor.posts_data.forEach((post) => {
      const publishedDate = getDateTimeFormated(post.published, true);

      dateInteractionCounts[publishedDate] =
        (dateInteractionCounts[publishedDate] || 0) +
        post.analytics.interactions;
    });

    const data: ChartDataPoint[] = Object.entries(dateInteractionCounts)
      .map(([date, interactions]) => ({
        x: date,
        y: interactions
      }))
      .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

    return {
      name: competitor.name, // <-- keep original competitor name
      data
    };
  });
}

export function collateCommentsByDate(
  competitors: CompetitorData[]
): { name: string; data: ChartDataPoint[] }[] {
  return competitors.map((competitor) => {
    const dateCommentCounts: Record<string, number> = {};

    competitor.posts_data.forEach((post) => {
      const publishedDate = getDateTimeFormated(post.published, true);

      dateCommentCounts[publishedDate] =
        (dateCommentCounts[publishedDate] || 0) + post.analytics.comments;
    });

    const data: ChartDataPoint[] = Object.entries(dateCommentCounts)
      .map(([date, comments]) => ({
        x: date,
        y: comments
      }))
      .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

    return {
      name: competitor.name,
      data
    };
  });
}

export function collatePostsByType(
  competitors: CompetitorData[]
): Array<{ name: string; value: number }> {
  const typeCounts: Record<string, number> = {
    reel: 0,
    video: 0,
    multi_image: 0,
    single_image: 0,
    post: 0
  };

  // Iterate through all competitors
  competitors.forEach((competitor) => {
    competitor.posts_data.forEach((post) => {
      let postType: string;

      if (post.type === 'reel') {
        postType = 'reel';
      } else if (post.type === 'video') {
        postType = 'video';
      } else if (post.type === 'multiimage') {
        postType = 'multi_image';
      } else if (post.type === 'post') {
        const mediaCount = post.media.length;

        if (mediaCount === 0) {
          postType = 'post';
        } else if (mediaCount === 1) {
          const mediaType = post.media[0].file_type;
          if (mediaType === 'video') {
            postType = 'video';
          } else if (mediaType === 'image') {
            postType = 'single_image';
          } else {
            postType = 'post';
          }
        } else {
          const hasVideo = post.media.some(
            (media) => media.file_type === 'video'
          );
          postType = hasVideo ? 'video' : 'multi_image';
        }
      } else {
        postType = 'post';
      }

      typeCounts[postType] = (typeCounts[postType] || 0) + 1;
    });
  });

  // Total count
  const total = Object.values(typeCounts).reduce((sum, v) => sum + v, 0);

  // Convert to array format with percentages
  const chartData = Object.entries(typeCounts)
    .filter(([, count]) => count > 0)
    .map(([name, count]) => ({
      name,
      value: Number(((count / total) * 100).toFixed(2)) // percentage as number
    }))
    .sort((a, b) => b.value - a.value);

  return chartData;
}

export function collatePostsByTypePerCompetitor(
  competitors: CompetitorData[]
): Array<{ label: string; data: Record<string, number> }> {
  return competitors.map((competitor) => {
    // fresh counter for this competitor
    const typeCounts: Record<string, number> = {
      Reel: 0,
      Video: 0,
      'Multi image': 0,
      'Single image': 0,
      Post: 0
    };

    competitor.posts_data.forEach((post) => {
      let postType: keyof typeof typeCounts;

      if (post.type === 'reel') {
        postType = 'Reel';
      } else if (post.type === 'video') {
        postType = 'Video';
      } else if (post.type === 'multiimage') {
        postType = 'Multi image';
      } else if (post.type === 'post') {
        const mediaCount = post.media.length;

        if (mediaCount === 0) {
          postType = 'Post';
        } else if (mediaCount === 1) {
          const mediaType = post.media[0].file_type;
          if (mediaType === 'video') {
            postType = 'Video';
          } else if (mediaType === 'image') {
            postType = 'Single image';
          } else {
            postType = 'Post';
          }
        } else {
          const hasVideo = post.media.some(
            (media) => media.file_type === 'video'
          );
          postType = hasVideo ? 'Video' : 'Multi image';
        }
      } else {
        postType = 'Post';
      }

      typeCounts[postType] = (typeCounts[postType] || 0) + 1;
    });

    return {
      label: competitor.name,
      data: typeCounts
    };
  });
}

export interface HeatmapDayData {
  title: string; // e.g. "Monday"
  data: Record<number, number>; // { hour: count }
}

export interface CompetitorHeatmap {
  name: string;
  data: HeatmapDayData[];
}

export function collatePostsByTime(
  competitors: CompetitorData[],
  t: any
): CompetitorHeatmap[] {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ];

  return competitors.map((competitor) => {
    // initialize day/hour buckets
    const dayHourCounts: Record<string, Record<number, number>> = {};
    days.forEach((day) => {
      dayHourCounts[day] = {};
      for (let h = 0; h < 24; h++) {
        dayHourCounts[day][h] = 0;
      }
    });

    competitor.posts_data.forEach((post) => {
      const date = new Date(post.published);
      const day = days[(date.getDay() + 6) % 7];
      const hour = date.getHours();
      dayHourCounts[day][hour] = (dayHourCounts[day][hour] || 0) + 1;
    });

    const data: HeatmapDayData[] = days.map((day) => ({
      title: day,
      data: dayHourCounts[day]
    }));

    return { name: competitor.name, data };
  });
}

export function collateInteractionsByTime(
  competitors: CompetitorData[],
  t: any
): CompetitorHeatmap[] {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ];

  return competitors.map((competitor) => {
    // initialize day/hour buckets
    const dayHourCounts: Record<string, Record<number, number>> = {};
    days.forEach((day) => {
      dayHourCounts[day] = {};
      for (let h = 0; h < 24; h++) {
        dayHourCounts[day][h] = 0;
      }
    });

    competitor.posts_data.forEach((post) => {
      const date = new Date(post.published);
      const day = days[(date.getDay() + 6) % 7];
      const hour = date.getHours();

      // safely grab interactions (null/undefined counts as 0)
      const interactions = post.analytics?.interactions ?? 0;

      dayHourCounts[day][hour] = (dayHourCounts[day][hour] || 0) + interactions;
    });

    const data: HeatmapDayData[] = days.map((day) => ({
      title: day,
      data: dayHourCounts[day]
    }));

    return { name: competitor.name, data };
  });
}

type DateValuePoint = { date: string; value: number };

type ClosestResult = {
  value: number;
  date: string; // the matched/closest point's date string
  index: number;
  exact: boolean;
  diffMs: number; // absolute difference between target and returned date
};

export function getValueAtOrClosestDate(
  target: string | Date | number,
  data: DateValuePoint[],
  opts?: { tieBreak?: 'earlier' | 'later' }
): ClosestResult | null {
  if (!data.length) return null;

  const tieBreak = opts?.tieBreak ?? 'earlier';
  const targetMs = toMs(target);

  // Precompute ms values once if you call this a lot (see note below).
  const getMsAt = (i: number) => Date.parse(data[i]!.date);

  // Clamp outside range
  const firstMs = getMsAt(0);
  const lastMs = getMsAt(data.length - 1);

  if (targetMs <= firstMs) {
    return {
      value: data[0]!.value,
      date: data[0]!.date,
      index: 0,
      exact: targetMs === firstMs,
      diffMs: Math.abs(targetMs - firstMs)
    };
  }

  if (targetMs >= lastMs) {
    const i = data.length - 1;
    return {
      value: data[i]!.value,
      date: data[i]!.date,
      index: i,
      exact: targetMs === lastMs,
      diffMs: Math.abs(targetMs - lastMs)
    };
  }

  // Binary search for insertion point
  let lo = 0;
  let hi = data.length - 1;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const midMs = getMsAt(mid);

    if (midMs === targetMs) {
      return {
        value: data[mid]!.value,
        date: data[mid]!.date,
        index: mid,
        exact: true,
        diffMs: 0
      };
    }

    if (midMs < targetMs) lo = mid + 1;
    else hi = mid - 1;
  }

  // Now: hi is the index < target, lo is the index > target (insertion point)
  const left = hi; // closest earlier
  const right = lo; // closest later

  // Prefer earlier date; if none, use later
  const bestIdx = left >= 0 ? left : right < data.length ? right : -1;

  if (bestIdx === -1) return null;

  const bestMs = getMsAt(bestIdx);

  return {
    value: data[bestIdx]!.value,
    date: data[bestIdx]!.date,
    index: bestIdx,
    exact: false,
    diffMs: Math.abs(targetMs - bestMs)
  };
}

function toMs(d: string | Date | number): number {
  if (typeof d === 'number') return d; // already ms
  if (d instanceof Date) return d.getTime();
  const ms = Date.parse(d);
  if (Number.isNaN(ms)) throw new Error(`Invalid date: ${d}`);
  return ms;
}
